import { supabase } from '@/integrations/supabase/client';
import { FarmPrice, CreateFarmPriceInput, FarmPriceFilters } from '@/types/database/farm-prices';

export const farmPriceService = {
  async getFarmPrices(filters?: FarmPriceFilters) {
    let query = supabase
      .from('farm_prices')
      .select('*')
      .order('date', { ascending: false });

    if (filters?.product_name) {
      query = query.eq('product_name', filters.product_name);
    }
    if (filters?.region) {
      query = query.ilike('region', `%${filters.region}%`);
    }
    if (filters?.supplier_name) {
      query = query.ilike('supplier_name', `%${filters.supplier_name}%`);
    }
    if (filters?.date_from) {
      query = query.gte('date', filters.date_from);
    }
    if (filters?.date_to) {
      query = query.lte('date', filters.date_to);
    }

    const { data, error } = await query.limit(10000);
    
    if (error) {
      console.error('Error fetching farm prices:', error);
      throw error;
    }
    
    return data as FarmPrice[];
  },

  async getFarmPrice(id: string) {
    const { data, error } = await supabase
      .from('farm_prices')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching farm price:', error);
      throw error;
    }

    return data as FarmPrice;
  },

  async createFarmPrice(input: CreateFarmPriceInput) {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('farm_prices')
      .insert({
        ...input,
        created_by: user?.id
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating farm price:', error);
      throw error;
    }

    return data as FarmPrice;
  },

  async updateFarmPrice(id: string, input: Partial<CreateFarmPriceInput>) {
    const { data, error } = await supabase
      .from('farm_prices')
      .update(input)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating farm price:', error);
      throw error;
    }

    return data as FarmPrice;
  },

  async deleteFarmPrice(id: string) {
    const { error } = await supabase
      .from('farm_prices')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting farm price:', error);
      throw error;
    }
  },

  async bulkCreateFarmPrices(prices: CreateFarmPriceInput[]) {
    const { data: { user } } = await supabase.auth.getUser();
    
    const pricesWithUser = prices.map(price => ({
      ...price,
      created_by: user?.id
    }));

    const { data, error } = await supabase
      .from('farm_prices')
      .insert(pricesWithUser)
      .select();

    if (error) {
      console.error('Error bulk creating farm prices:', error);
      throw error;
    }

    return data as FarmPrice[];
  }
};
